import { inheritedValue, inheritanceChain } from "./stageTargets";
import { sameParameterValue } from "./xmlEntities";
// transport-config-helpers.js
//
// Pure helpers for the configuration step of the package transport wizard. The older ones were
// ported from the modal wizard in IntegrationContent.jsx; effektiverBasiswert and
// buildConfigsByWerk belong to the snapshot contract and have no counterpart there.
//
// No React, no hooks, no stores, no imports, so the module stays testable and tree-shakeable.
// Defensive against null/undefined input.

// ---------------------------------------------------------------------------
// Bekannte Auswahlfelder der SAP-Adapter. SAP speichert und erwartet den technischen Schlüssel
// (z. B. `default`), der Designer zeigt die Bezeichnung („Internet“). Das Backend liefert je
// Parameter `adapterField` (ifl:property-Schlüssel, an den der Parameter gebunden ist); nur für
// die hier gelisteten Felder wird eine Auswahl angeboten und eine Bezeichnung auf den Schlüssel
// umgesetzt. Alle anderen Felder bleiben Freitext. Erweiterbar je Feld.
export const ADAPTER_FIELD_OPTIONS = {
  proxyType: {
    label: { de: "Proxy Type", en: "Proxy Type" },
    options: [
      { value: "default", label: { de: "Internet", en: "Internet" }, aliases: ["internet"] },
      { value: "sapcc", label: { de: "On-Premise", en: "On-Premise" }, aliases: ["onpremise", "sapcloudconnector", "cloudconnector"] },
      { value: "manual", label: { de: "Manuell", en: "Manual" }, aliases: ["manual", "manuell"] },
    ],
  },
};

// Vergleichsform: Groß/Klein, Leerzeichen, Bindestrich und Unterstrich spielen keine Rolle.
const foldAdapterValue = s => String(s == null ? "" : s).toLowerCase().replace(/[\s\-_]+/g, "");

/**
 * Optionen eines bekannten Auswahlfelds, sonst null (Freitext).
 * @param {string|undefined} adapterField ifl:property-Schlüssel aus dem Backend, z. B. "proxyType".
 * @returns {Array<{value:string,label:object,aliases:string[]}>|null}
 */
export function adapterFieldOptions(adapterField) {
  return (adapterField && ADAPTER_FIELD_OPTIONS[adapterField]?.options) || null;
}

/**
 * Anzeigename des Felds (z. B. "Proxy Type"), Rückfall auf den Schlüssel.
 */
export function adapterFieldName(adapterField, lang) {
  const def = adapterField && ADAPTER_FIELD_OPTIONS[adapterField];
  return def ? (def.label[lang] || def.label.de) : String(adapterField || "");
}

/**
 * Setzt einen Wert eines bekannten Auswahlfelds auf den SAP-Schlüssel um.
 *
 *   - leer                      -> { value:"", valid:true, mapped:false }  (nicht gesetzt)
 *   - Schlüssel (`default`)     -> unverändert, valid
 *   - Bezeichnung („Internet“)  -> { value:"default", valid:true, mapped:true, from:"Internet" }
 *   - sonst                     -> { value:<unverändert>, valid:false }    (wird NICHT still ersetzt)
 *   - unbekanntes Feld          -> unverändert, valid (Freitext)
 *
 * @param {string|undefined} adapterField
 * @param {*} value
 * @returns {{value:string, valid:boolean, mapped:boolean, from?:string}}
 */
export function normalizeAdapterFieldValue(adapterField, value) {
  const raw = value == null ? "" : String(value);
  const options = adapterFieldOptions(adapterField);
  if (!options || raw.trim() === "") return { value: raw, valid: true, mapped: false };
  const folded = foldAdapterValue(raw);
  const byKey = options.find(o => o.value === raw) || options.find(o => foldAdapterValue(o.value) === folded);
  if (byKey) return byKey.value === raw ? { value: raw, valid: true, mapped: false } : { value: byKey.value, valid: true, mapped: true, from: raw };
  const byLabel = options.find(o => o.aliases.includes(folded) || Object.values(o.label).some(l => foldAdapterValue(l) === folded));
  if (byLabel) return { value: byLabel.value, valid: true, mapped: true, from: raw };
  return { value: raw, valid: false, mapped: false };
}

/**
 * Anzeige eines Werts mit Bezug zum SAP-Schlüssel, z. B. "Internet (default)". Unbekannte Werte
 * und Freitextfelder liefern den Wert unverändert.
 */
export function adapterFieldValueLabel(adapterField, value, lang) {
  const options = adapterFieldOptions(adapterField);
  const raw = value == null ? "" : String(value);
  if (!options || !raw) return raw;
  const opt = options.find(o => o.value === raw);
  return opt ? `${opt.label[lang] || opt.label.de} (${opt.value})` : raw;
}

/**
 * Normalisiert die vorbelegten Werte eines Eintrags (Basis und Stufenabweichungen) auf den
 * SAP-Schlüssel. Ein umgesetzter Wert bekommt `mappedFrom` (Hinweis im UI); unbekannte Werte
 * bleiben stehen und werden über classifyConfigStatus als "invalid" gemeldet.
 * Am Format ändert sich nichts außer newValue (und dem Hinweisfeld mappedFrom).
 */
export function normalizeEntryAdapterValues(entry) {
  if (!entry || !adapterFieldOptions(entry.adapterField)) return entry;
  const apply = cell => {
    if (!cell || typeof cell !== "object") return cell;
    const n = normalizeAdapterFieldValue(entry.adapterField, cell.newValue);
    return n.mapped ? { ...cell, newValue: n.value, mappedFrom: n.from } : cell;
  };
  const root = apply(entry);
  if (!entry.byStage) return root;
  const byStage = {};
  for (const [key, cell] of Object.entries(entry.byStage)) byStage[key] = apply(cell);
  return { ...root, byStage };
}

/**
 * Baut den Cache-Key für eine Artefakt-Konfiguration so wie der Modal-Wizard
 * (IntegrationContent.jsx Z. 1153).
 *
 * Format:
 *   - mit werkKennung: "artId__werkKennung"
 *   - ohne werkKennung, mit targetId: "artId__targetId"
 *   - sonst: "artId"
 *
 * @param {string} artId            Artefakt-ID (z.B. Flow-ID).
 * @param {string} [targetId]       Ziel-Instanz-ID, optional.
 * @param {string} [werkKennung]    Werk-Kennung (z.B. "werk__kennung"), optional.
 *                                  Hat Vorrang vor targetId.
 * @returns {string} Cache-Key oder "" falls artId fehlt.
 *
 * @example
 *   getArtifactCacheKey("Flow1", "tgt-1");           // "Flow1__tgt-1"
 *   getArtifactCacheKey("Flow1", "tgt-1", "werk_DE"); // "Flow1__werk_DE"
 *   getArtifactCacheKey("Flow1");                     // "Flow1"
 */
export function getArtifactCacheKey(artId, targetId, werkKennung) {
  if (!artId) return "";
  if (werkKennung) return `${artId}__${werkKennung}`;
  if (targetId) return `${artId}__${targetId}`;
  return String(artId);
}

/**
 * Normalisiert einen Configurations-Eintrag aus dem Backend zur Modal-Form
 * (IntegrationContent.jsx Z. 1166-1170).
 *
 * Hinzugefuegte Felder:
 *   - newValue:     targetValue || ""
 *   - fromTarget:   !!targetValue
 *   - defaultValue: ParameterValue
 *   - modified:     false (Initialzustand vor User-Edit)
 *
 * @param {object|null|undefined} raw Roh-Eintrag vom Backend.
 * @returns {object|null} Normalisierter Eintrag oder null falls raw leer.
 *
 * @example
 *   normalizeConfigEntry({ ParameterKey: "host", ParameterValue: "x", targetValue: "y" })
 *   // -> { ParameterKey:"host", ParameterValue:"x", targetValue:"y",
 *   //      newValue:"y", fromTarget:true, defaultValue:"x", modified:false }
 */
export function normalizeConfigEntry(raw) {
  if (!raw || typeof raw !== "object") return null;
  const targetValue = raw.targetValue;
  return {
    ...raw,
    newValue: targetValue || "",
    fromTarget: !!targetValue,
    defaultValue: raw.ParameterValue,
    modified: false,
  };
}

/**
 * Klassifiziert den Status einer Config-Zeile für das UI-Label.
 *
 * Reihenfolge der Regeln (frueher Match gewinnt):
 *   1. !newValue UND required === true          -> "required"
 *      (Pflicht nur, wenn SAP den Parameter in parameters.propdef mit
 *      isRequired=true definiert; das Backend liefert dafür `required`)
 *   2. !newValue                                -> "empty" (Warnung, nie Blocker)
 *   3. fromTarget UND newValue===targetValue    -> "fromTarget"
 *   4. newValue !== defaultValue                -> "modified"
 *   5. sonst                                    -> "default"
 *
 * @param {object|null|undefined} c Normalisierter Config-Eintrag.
 * @returns {"required"|"fromTarget"|"modified"|"default"|"empty"}
 *
 * @example
 *   classifyConfigStatus({ group:"sender", newValue:"", required:true });
 *   // -> "required"
 *   classifyConfigStatus({ group:"sender", newValue:"" });
 *   // -> "empty"
 *   classifyConfigStatus({ group:"other", newValue:"x", defaultValue:"x" });
 *   // -> "default"
 */
export function classifyConfigStatus(c) {
  if (!c || typeof c !== "object") return "empty";
  const newValue = c.newValue;
  const fromTarget = !!c.fromTarget;
  const defaultValue = c.defaultValue;
  const targetValue = c.targetValue;
  // hasTargetValue: Parameter existiert bereits im Ziel-Tenant (Backend hat
  // targetValue geliefert). Solche Parameter sind NIE Pflichtfeld - der
  // bestehende Wert am Ziel bleibt erhalten wenn der User nichts angibt.
  const hasTargetValue = targetValue !== undefined && targetValue !== null && targetValue !== "";
  if (!newValue) {
    // Sender und Receiver sind NICHT automatisch Pflicht: SAP kennzeichnet z. B. XI_KeepAlive oder
    // XI_ReceiverCommunicationParty mit isRequired=false. Fehlt `required` (propdef nicht lesbar), gilt keine Pflicht.
    if (c.required === true && !hasTargetValue && !fromTarget) return "required";
    return "empty";
  }
  // Bekanntes Auswahlfeld (z. B. Proxy Type) mit einem Wert, der weder Schlüssel noch Bezeichnung
  // ist: SAP lehnt ihn beim Deploy ab. Kein Blocker, aber als ungültig gemeldet.
  if (c.adapterField && !normalizeAdapterFieldValue(c.adapterField, newValue).valid) return "invalid";
  // E: entschlüsselter Vergleich — `&lt;` und `<` sind derselbe Wert.
  if (fromTarget && sameParameterValue(newValue, targetValue)) return "fromTarget";
  if (!sameParameterValue(newValue, defaultValue)) return "modified";
  return "default";
}

/**
 * The value that applies to the first target stage and is inherited by all later stages.
 *
 * Sender and receiver parameters deliberately do NOT inherit the source value: an address from
 * development would simply be wrong in the target, so the field stays empty. It only counts as a
 * required field when SAP marks the parameter with isRequired=true; otherwise it shows a warning.
 *
 * @param {object} c Normalized config entry.
 * @returns {string} Base value, "" if none applies.
 */
export function effektiverBasiswert(c) {
  if (!c) return "";
  if (c.newValue) return c.newValue;
  // A2: Der Benutzer hat den Wert ausdrücklich geleert (modified=true). Nicht durch den
  // Quellwert ersetzen — der Payload soll "" senden, damit der Backend-Grabstein greift.
  if (c.modified) return "";
  const istAdresse = c.group === "sender" || c.group === "receiver";
  return istAdresse ? "" : (c.defaultValue || "");
}

/**
 * Builds the parameter structure the cicd-snapshot endpoint expects as `configsByWerk`.
 *
 * Target shape:
 *   {
 *     "<kennung>" | "": {                       // "" = no plant split
 *       "<source-artifact-id>": [
 *         { ParameterKey, DataType, Description,
 *           values: { "__base__": "...", "<stageKey>": "..." } }
 *       ]
 *     }
 *   }
 *
 * The backend turns this into SAP's file: `__base__` becomes ParameterValues.Default, every stage
 * key becomes the GitHub environment name (qas -> QAS, preprod -> PRE-PROD).
 *
 * Two intentional differences from the earlier overlay format:
 *  - The plant level sits OUTERMOST instead of inside the file, because in Git every plant gets
 *    its own package folder with its own file.
 *  - EVERY parameter that has a value is written, not just the changed ones: SAP's read rule falls
 *    back to ParameterValues.Default, and without it the parameter stays unset in the target.
 *
 * Stage values equal to the base value are left out - they would only bloat the Git diff and SAP's
 * fallback yields the same value. A `null` value, by contrast, is the tombstone: the stage was in
 * Git and the user cleared it, so the backend deletes it. A MISSING key still means "not reported,
 * keep it"; without that distinction a removed deviation would stay forever.
 *
 * @param {object} state    TransportV2 reducer state (state.cfg and state.gitCfg).
 * @param {Array}  [stages] Forward stages from vorwaertsStufen(state). The first one is the base.
 * @param {object} [gitCfg] Preloaded Git state; without it state.gitCfg is used.
 * @returns {object} Parameter map per plant (empty when nothing is configured).
 */
export function buildConfigsByWerk(state, stages, gitCfg, forwardStages) {
  const out = {};
  const cfg = state && state.cfg;
  if (!cfg || typeof cfg !== "object") return out;
  const stufen = Array.isArray(stages) ? stages : [];
  // First forward stage = base. All later ones are possible deviations.
  const overlayStufen = stufen.slice(1);
  const git = gitCfg || (state && state.gitCfg) || {};
  for (const [werkKey, artefactMap] of Object.entries(cfg)) {
    // "std" means no plant split. In the backend that is the empty kennung.
    const kennung = werkKey === "std" ? "" : werkKey;
    for (const [artId, item] of Object.entries(artefactMap || {})) {
      // G: Nur ausgewählte Artefakte je Werk senden. state.sel[artId][werkKey] = true.
      if (state.sel && !state.sel[artId]?.[werkKey]) continue;
      const entries = (item && item.entries) || [];
      const params = [];
      for (const c of entries) {
        if (!c || !c.ParameterKey) continue;
        const values = {};
        // Bekanntes Auswahlfeld: der Payload trägt immer den SAP-Schlüssel („Internet“ -> default).
        // Ein unbekannter Wert bleibt unverändert (kein stilles Ersetzen).
        const basis = normalizeAdapterFieldValue(c.adapterField, effektiverBasiswert(c)).value;
        // What is already in Git for this parameter? Only that tells "deleted" apart from
        // "never maintained".
        const inGit = git?.[werkKey]?.[artId]?.[c.ParameterKey]?.values || null;
        // Vertrag v30: JEDER Parameter sendet seine Basis — ist sie leer (nicht konfiguriert
        // oder vom Benutzer geleert), als __base__:"". Nur so steht in Git ausdrücklich
        // „nicht konfiguriert" und der Workflow setzt das Ziel leer. Stufenwerte weiterhin
        // nur bei echter Abweichung bzw. vorhandenem Git-Stufenschlüssel.
        values.__base__ = basis !== "" ? basis : "";
        for (const st of overlayStufen) {
          const wert = normalizeAdapterFieldValue(c.adapterField, c.byStage && c.byStage[st.key] ? c.byStage[st.key].newValue : "").value;
          // Zusatzziel (Schlüssel mit "@", Vertrag V.11): weggelassen wird nur ein Wert gleich dem Kettenwert, nicht gleich der Basis.
          const vergleich = String(st.key).includes("@") && forwardStages
            ? inheritedValue(Object.fromEntries(stufen.map(item => {
              const v = c.byStage?.[item.key]?.newValue;
              return [item.key, v == null ? v : normalizeAdapterFieldValue(c.adapterField, v).value];
            })),
              inheritanceChain(forwardStages, st.stageKey, st.targetId).filter(key => key !== stufen[0]?.key)) ?? basis
            : basis;
          // E: entschlüsselter Vergleich — `&lt;` und `<` sind derselbe Wert.
          const leer = wert == null || wert === "" || sameParameterValue(wert, vergleich);
          if (leer) {
            // Tombstone: the stage was in Git and is now empty, so the user removed the
            // deviation on purpose. Without this null the old value would stay and the stage
            // would silently keep running a value nobody wants any more.
            if (inGit && inGit[st.key] != null) values[st.key] = null;
            continue;
          }
          values[st.key] = wert;
        }
        if (!Object.keys(values).length) continue;
        params.push({
          ParameterKey: c.ParameterKey,
          DataType: c.DataType || "xsd:string",
          Description: c.Description || "",
          values,
        });
      }
      if (!params.length) continue;
      if (!out[kennung]) out[kennung] = {};
      out[kennung][artId] = params;
    }
  }
  return out;
}

/**
 * Pflichtfeld-Validierung analog zur Pipeline-Prüfung im Modal
 * (IntegrationContent.jsx Z. 1207-1217).
 *
 * Pflichtfeld = Eintrag mit required === true (SAP propdef isRequired) und leerem newValue.
 *
 * @param {Array|null|undefined} cfgs Liste normalisierter Configs.
 * @returns {{ok:boolean, missing:string[]}} ok=true wenn alles ausgefüllt,
 *          missing enthält die ParameterKeys der leeren Pflichtfelder.
 *
 * @example
 *   validateRequiredFields([
 *     { ParameterKey:"host", group:"sender",   newValue:"" },
 *     { ParameterKey:"k",    group:"receiver", newValue:"x" }
 *   ]);
 *   // -> { ok:false, missing:["host"] }
 */
export function validateRequiredFields(cfgs) {
  // Keine clientseitige Sperre: Pflicht ist nur, was SAP in parameters.propdef mit
  // isRequired=true definiert (classifyConfigStatus "required"). Scene4Config fragt dafür vor
  // "Weiter" nach; den Transport selbst prüft der Ziel-Tenant beim Deploy.
  return { ok: true, missing: [] };
}

/**
 * Ob mindestens ein Parameter leer ist, den SAP in parameters.propdef mit isRequired=true
 * definiert. Sender und Receiver sind nicht automatisch Pflicht.
 *
 * @param {Array|null|undefined} cfgs Liste normalisierter Configs.
 * @returns {boolean}
 */
export function hasEmptyRequiredParam(cfgs) {
  return Array.isArray(cfgs) && cfgs.some(c => !!c && !c.newValue && c.required === true);
}

/**
 * Leerer Checkbox-Parameter (xsd:boolean): SAP liest einen leeren Wert in parameters.prop als
 * Adapter-Standard, die Integration Suite zeigt dann den Standard des Adapters, und er gilt zur Laufzeit.
 *
 * @param {object|null|undefined} c Normalisierter Config-Eintrag.
 * @returns {boolean}
 */
export function isEmptyBooleanAdapterDefault(c) {
  return !!c && !c.newValue && String(c.DataType || "").toLowerCase() === "xsd:boolean";
}

/**
 * Buckets Configs nach group in genau drei Sammeltoepfe: sender, receiver, other.
 * Bewusst KEIN processing- und KEIN credentials-Bucket (UI-Entscheidung im Modal-
 * Wizard - diese Gruppen sollen nicht eigenstaendig sichtbar sein).
 *
 * @param {Array|null|undefined} cfgs Liste normalisierter Configs.
 * @returns {{sender:Array, receiver:Array, other:Array}}
 *
 * @example
 *   bucketByGroup([
 *     {ParameterKey:"a", group:"sender"},
 *     {ParameterKey:"b", group:"receiver"},
 *     {ParameterKey:"c", group:"processing"},
 *     {ParameterKey:"d"} // ohne group
 *   ]);
 *   // -> { sender:[a], receiver:[b], other:[c,d] }
 */
export function bucketByGroup(cfgs) {
  const sender = [];
  const receiver = [];
  const other = [];
  if (!cfgs || !Array.isArray(cfgs)) return { sender, receiver, other };
  for (const c of cfgs) {
    if (!c) continue;
    if (c.group === "sender") sender.push(c);
    else if (c.group === "receiver") receiver.push(c);
    else other.push(c);
  }
  return { sender, receiver, other };
}

/**
 * Extrahiert den Adapter-Namen aus dem ersten Eintrag mit gesetztem adapterType.
 *
 * @param {Array|null|undefined} cfgs Liste normalisierter Configs.
 * @returns {string} Adapter-Name oder "" wenn keiner gefunden.
 *
 * @example
 *   detectAdapter([{adapterType:""}, {adapterType:"HTTPS"}, {adapterType:"SOAP"}]);
 *   // -> "HTTPS"
 */
export function detectAdapter(cfgs) {
  if (!cfgs || !Array.isArray(cfgs)) return "";
  for (const c of cfgs) {
    if (c && c.adapterType) return String(c.adapterType);
  }
  return "";
}

/**
 * BONUS: Baut die Backend-URL für artifact-configs analog zum Modal-Wizard
 * (IntegrationContent.jsx Z. 1162-1164).
 *
 * URL-Schema:
 *   /api/{sourceIid}/transport/artifact-configs/{flowId}
 *   [?targetInstanceId=X&targetFlowId=Y]
 *
 * Query-String wird NUR angehaengt, wenn opts.isUpdate=true UND opts.targetInstanceId
 * gesetzt ist. opts.targetFlowId faellt auf flowId zurück.
 *
 * @param {string} sourceIid          Quell-Instanz-ID.
 * @param {string} flowId             Quell-Artefakt-ID.
 * @param {object} [opts]
 * @param {string} [opts.targetInstanceId]
 * @param {string} [opts.targetFlowId]
 * @param {boolean}[opts.isUpdate]    Default false.
 * @returns {string} Vollstaendige URL.
 *
 * @example
 *   buildArtifactConfigsUrl("src", "Flow1",
 *     { targetInstanceId:"tgt", targetFlowId:"Flow1_DE", isUpdate:true });
 *   // -> "/api/src/transport/artifact-configs/Flow1?targetInstanceId=tgt&targetFlowId=Flow1_DE"
 */
export function buildArtifactConfigsUrl(sourceIid, flowId, opts) {
  const base = `/api/${sourceIid}/transport/artifact-configs/${encodeURIComponent(flowId)}`;
  if (!opts || !opts.isUpdate || !opts.targetInstanceId) return base;
  const tgtFlow = opts.targetFlowId || flowId;
  return `${base}?targetInstanceId=${opts.targetInstanceId}&targetFlowId=${encodeURIComponent(tgtFlow)}`;
}

// ---------------------------------------------------------------------------
// Self-Test (auskommentiert - nur als Referenz für Reviewer):
//
// const sample = [
//   { ParameterKey:"host",  group:"sender",   ParameterValue:"old", targetValue:"new", DataType:"xsd:string", adapterType:"HTTPS" },
//   { ParameterKey:"port",  group:"other",    ParameterValue:"443", targetValue:"",    DataType:"xsd:string" },
//   { ParameterKey:"creds", group:"receiver", ParameterValue:"u",   targetValue:"",    DataType:"xsd:string" }
// ];
// const normalized = sample.map(normalizeConfigEntry);
// console.log("cacheKey ", getArtifactCacheKey("Flow1", "tgt", "werk_DE"));
// console.log("status   ", normalized.map(classifyConfigStatus));
// console.log("basis    ", normalized.map(effektiverBasiswert));
// console.log("validate ", validateRequiredFields(normalized));
// console.log("buckets  ", bucketByGroup(normalized));
// console.log("adapter  ", detectAdapter(normalized));
// console.log("url      ", buildArtifactConfigsUrl("src","Flow1",{targetInstanceId:"tgt",targetFlowId:"Flow1_DE",isUpdate:true}));
